import { FeatureFlag } from '@cbo/shared-library';
import { SiteConfiguration, SitesSwitcher, useSiteContext } from '@ncr-voyix-commerce/react-common-components';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import { isEqual } from 'lodash';
import routes, { baseRoutes, siteLevelModifyRoutes } from '../../constants/routes';
import { useSites } from '../../contexts/siteContext';
import { useUsers } from '../../contexts/userContext';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';
import {
  isAllSitesRoute,
  isAuditHierarchyCfcRoute,
  isCfcOrgViewRoute,
  isExcludeSiteUpdateRoute,
  isOrgViewRoute,
  isAllSitesRouteWithOldPermissions,
  shouldDisableSiteSelection,
} from '../../admin/utils/siteUtils';
import isPermitted from '../../lib/permissions';
import useCfcOrg from '../../utils/hooks/useCfcOrg';

export enum EuStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type SiteData = {
  enterpriseUnitId: string;
  name?: string;
  referenceId?: string;
  timeZone?: string;
  id?: string;
  bslSiteId?: string;
};
export enum SiteSwitcherOptions {
  ORGANIZATION_VIEW = 'organizationView',
}
const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;

function BspSiteSwitcherWrapper() {
  const { t } = useTranslation();
  const { pathname: currentUrl } = useLocation();
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const user = useUsers();
  const { isSiteFiltering } = useSites();
  const usagePerFeatureFlag = useFeatureFlag(FeatureFlag.usagePer);
  const iseuLevelRolesEnabled = useFeatureFlag(FeatureFlag.euLevelRoles);
  const { selectedSite, siteConfiguration, updateSiteConfiguration, updateSelectedSiteFromId } = useSiteContext();
  const { updateSelectedSite: updateOldSelectedSite, selectedSite: oldSelectedSite } = useSites();
  const isOrgRoute = isOrgViewRoute(currentUrl);
  const { isCfcOrg } = useCfcOrg();
  const enableParentSiteSelection = isCfcOrgViewRoute(currentUrl) && isCfcOrg;
  // This isLoaded will helps to update already selected site  from local storage in first time
  useEffect(() => {
    if (selectedSite && !isOrgRoute && isLoaded) {
      const site = {
        name: selectedSite.displayName,
        enterpriseUnitId: selectedSite.id,
        referenceId: selectedSite.eu_parentId,
      };
      updateOldSelectedSite(site);
    } else if (selectedSite?.id === SiteSwitcherOptions.ORGANIZATION_VIEW && !isOrgRoute && isLoaded) {
      const site = {
        name: oldSelectedSite.name,
        enterpriseUnitId: oldSelectedSite.enterpriseUnitId,
        referenceId: oldSelectedSite.referenceId,
        bslSiteId: selectedSite.id,
      };
      updateOldSelectedSite(site);
    }
    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  useEffect(() => {
    const baseRoute = baseRoutes.find((path) => currentUrl.includes(path));
    const isSiteLevelModifyRoute = (usagePerFeatureFlag ? siteLevelModifyRoutes : []).some(
      (route) => !!matchPath(currentUrl, { path: route, exact: true })
    );
    if (enableParentSiteSelection) {
      setIsDisabled(false);
      // Check if the current route has a GUID, which will inevitably be site specific. If yes disable selecting the site
    } else if (isSiteFiltering || (baseRoute && uuidRegex.test(currentUrl)) || isSiteLevelModifyRoute || isOrgRoute) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [currentUrl, isSiteFiltering, usagePerFeatureFlag, enableParentSiteSelection, isOrgRoute]);

  const setSelectedSite = () => {
    const currentSite = localStorage.getItem(`${user.id}-${user.org?.bslId}-selectedSite`);
    const siteData: SiteData = currentSite ? JSON.parse(currentSite) : undefined;
    if (isOrgRoute) {
      // Can no longer set org view directly with ^v1.2.3 of site switcher
      // Need to further research if there is an alternative way to set org view or to remove this logic
    } else if (
      // if page refresh or navigate from another page if site is org veiw it will set org view
      isAllSitesRoute(currentUrl) &&
      siteData &&
      [selectedSite?.id, siteData?.bslSiteId].includes(SiteSwitcherOptions.ORGANIZATION_VIEW)
    ) {
      // isLoaded helps to set the site in intial time
      // Can no longer set org view directly with ^v1.2.3 of site switcher
      // Need to further research if there is an alternative way to set org view or to remove this logic
    } else if (
      siteData &&
      siteData?.enterpriseUnitId !== '' &&
      (siteData?.enterpriseUnitId !== selectedSite?.id || !isLoaded) &&
      currentUrl !== routes.HOME_PAGE &&
      ![selectedSite?.id, siteData?.bslSiteId].includes(SiteSwitcherOptions.ORGANIZATION_VIEW) &&
      !isOrgRoute
    ) {
      updateSelectedSiteFromId(siteData?.enterpriseUnitId);
    } else if (siteData && siteData?.enterpriseUnitId !== '') {
      updateSelectedSiteFromId(siteData?.enterpriseUnitId);
    } else if (siteData && siteData.enterpriseUnitId && siteData.enterpriseUnitId === selectedSite?.id) {
      updateSelectedSiteFromId(selectedSite?.id);
    }
  };

  // It helps to set the already selected site to new site selector
  useEffect(() => {
    setSelectedSite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear inputSiteId on Org View so the next siteId set is a real change and triggers refetch for BSL Site switcher.
    if (selectedSite?.id === SiteSwitcherOptions.ORGANIZATION_VIEW) {
      updateSelectedSiteFromId(undefined);
    }
  }, [selectedSite?.id, updateSelectedSiteFromId]);

  const updateSiteConfig = (config: SiteConfiguration) => {
    if (!isEqual(siteConfiguration, config)) {
      updateSiteConfiguration({
        ...config,
      });
    }
  };

  useEffect(() => {
    const allSiteRoute = iseuLevelRolesEnabled
      ? isAllSitesRoute(currentUrl)
      : isAllSitesRouteWithOldPermissions(currentUrl);

    if (shouldDisableSiteSelection(currentUrl, isCfcOrg)) {
      updateSiteConfig({
        ...siteConfiguration,
        allowOrganizationView: false,
        allowParentSiteSelection: false,
      });
      setIsDisabled(true);
    } else if (allSiteRoute) {
      const { euPermission, orgPermission } = allSiteRoute;
      const hasSiteAccess = isPermitted(user, euPermission, false);
      const hasOrgAccess = isPermitted(user, orgPermission, false);
      if (hasSiteAccess && hasOrgAccess) {
        updateSiteConfig({
          ...siteConfiguration,
          allSitesMode: false,
          allowOrganizationView: true,
          allowParentSiteSelection: false,
        });
        setSelectedSite();
      } else if (hasSiteAccess) {
        updateSiteConfig({
          ...siteConfiguration,
          allowOrganizationView: false,
          allowParentSiteSelection: false,
        });
        setSelectedSite();
      } else if (hasOrgAccess) {
        updateSiteConfig({
          ...siteConfiguration,
          allowOrganizationView: false,
          allowParentSiteSelection: false,
        });
        setIsDisabled(true);
      }
    } else if (enableParentSiteSelection) {
      updateSiteConfig({
        allSitesMode: false,
        allowParentSiteSelection: true,
        allowOrganizationView: false,
      });
      setSelectedSite();
    } else if (!isExcludeSiteUpdateRoute(currentUrl)) {
      updateSiteConfig({
        ...siteConfiguration,
        allSitesMode: isOrgRoute,
        allowOrganizationView: isOrgRoute,
        allowParentSiteSelection: false,
      });
      setSelectedSite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrl, isOrgRoute, enableParentSiteSelection, siteConfiguration, isCfcOrg]);

  const isAuditHierarchyRoute = isAuditHierarchyCfcRoute(currentUrl) && isCfcOrg;
  if (isAuditHierarchyRoute) {
    // On the Audit Log page the in-page SitesSwitcher owns selection. The AppBar renders a static
    // disabled 'All sites' stub so it never reflects selectedSite changes. See ASM-9140.
    return (
      <Tooltip title={t('globalFilters.allSites')}>
        <span>
          <Button
            data-testid='appbar-all-sites-stub'
            disabled
            variant='text'
            size='small'
            sx={{ fontWeight: 600, textTransform: 'none', opacity: 0.5 }}
          >
            {t('globalFilters.allSites')}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <SitesSwitcher
      data-testid='sites-switcher'
      config={{
        isDisabled,
      }}
    />
  );
}

export default BspSiteSwitcherWrapper;
